import {
  AlertProps,
  Snackbar,
  Alert as MuiAlert,
  Box,
  SxProps
} from '@mui/material';
import React, { useEffect } from 'react';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import InfoIcon from '@mui/icons-material/Info';
import deepmerge from '@mui/utils/deepmerge';
import { lightTheme } from '../../theme/index';
import styles from './styles';

export interface LibAlertProps extends AlertProps {
  open: boolean;
  autoHideAfter?: number;
  autoHideEnable?: boolean;
  title?: string;
  description?: string | Element;
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right' | 'center';
  };
  severity: 'success' | 'warning' | 'error' | 'info';
  actionButton?: {
    title: string;
    linkTo: string;
    function: () => void;
  };
  children?: JSX.Element | null;
}
const { palette } = lightTheme;

export function InfoMessageBar({
  description,
  variant = 'filled',
  severity = 'success',
  children = null,
  containerStyles = null,
  title,
  action,
  ...props
}: AlertProps & {
  description?: string;
  action?: string;
  styles: SxProps;
  containerStyles: SxProps | null;
}) {
  const content = description ? (
    <Box sx={{ ...props.styles, ...styles.description }}>{description}</Box>
  ) : (
    children
  );
  const heading = title ? (
    <Box sx={{ ...props.styles, ...styles.title }}>{title}</Box>
  ) : null;

  useEffect(() => {
    // Apply additional styles when the component mounts
    const parentElement = document.querySelector('.MuiSnackbar-root > div');

    if (parentElement) {
      parentElement.style.width = '100%';
    }
  }, []);

  return (
    <MuiAlert
      variant={variant}
      severity={severity}
      iconMapping={{
        success: props.iconMapping?.success ? (
          props.iconMapping.success
        ) : (
          <CheckRoundedIcon sx={{ color: palette.seaGreen.main }} />
        ),
        warning: props.iconMapping?.warning ? (
          props.iconMapping.warning
        ) : (
          <WarningAmberRoundedIcon sx={{ color: palette.secondary['300'] }} />
        ),
        error: props.iconMapping?.error ? (
          props.iconMapping.error
        ) : (
          <InfoIcon sx={{ color: palette.bloodRed[100] }} />
        ),
        info: props.iconMapping?.info ? (
          props.iconMapping.info
        ) : (
          <InfoIcon sx={{ color: palette.grey.main }} />
        )
      }}
      sx={deepmerge(
        { ...styles.base },
        deepmerge(styles[severity], {
          ...containerStyles
        })
      )}
      {...props}
    >
      {title && heading}
      {content}
      {action && action}
    </MuiAlert>
  );
}

function LibAlert({
  open,
  autoHideAfter = 5000,
  anchorOrigin = { vertical: 'top', horizontal: 'center' },
  variant = 'filled',
  severity = 'success',
  autoHideEnable = false,
  children = null,
  ...props
}: LibAlertProps) {
  const handleClose = (event: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    props.onClose(event);
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideEnable ? autoHideAfter : null}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
    >
      <Box>
        <InfoMessageBar variant={variant} severity={severity} {...props}>
          {children}
        </InfoMessageBar>
      </Box>
    </Snackbar>
  );
}

export const Alert = React.memo(LibAlert);

export default Alert;
